/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { ConfidenceRing } from "./ConfidenceRing";
import { cn } from "../../lib/utils";

/**
 * The agent's confidence, decomposed. When a reply is routed to a human, this is the "why": each
 * component score (0..1) the gate weighed before it paused. An empty bar reads at a glance as the
 * factor that held the score back.
 */

const ORDER = [
  "RANKING_SCORE",
  "PROFILE_COMPLETENESS",
  "PRIOR_ENGAGEMENT",
  "TIME_TO_RESPONSE",
  "ELIGIBILITY_FRESHNESS",
];

const LABELS: Record<string, string> = {
  RANKING_SCORE: "Ranking",
  PROFILE_COMPLETENESS: "Profile completeness",
  PRIOR_ENGAGEMENT: "Prior engagement",
  TIME_TO_RESPONSE: "Response time",
  ELIGIBILITY_FRESHNESS: "Eligibility freshness",
};

function label(key: string): string {
  return (
    LABELS[key] ??
    key
      .replaceAll("_", " ")
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase())
  );
}

function ordered(breakdown: Record<string, number>): [string, number][] {
  const known = new Set(ORDER);
  const head = ORDER.filter((key) => key in breakdown);
  const tail = Object.keys(breakdown)
    .filter((key) => !known.has(key))
    .sort();
  return [...head, ...tail].map((key) => [key, clamp(Number(breakdown[key]))]);
}

function clamp(value: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}

function tier(value: number): string {
  if (value >= 0.7) {
    return "High confidence";
  }
  if (value >= 0.4) {
    return "Medium confidence";
  }
  return "Low confidence";
}

export function ConfidenceBreakdown({
  breakdown,
  confidence,
  className,
}: {
  breakdown: Record<string, number>;
  confidence: number | null;
  className?: string;
}) {
  const rows = ordered(breakdown);
  const weakest = rows.reduce<string | null>(
    (lowest, [key, score]) =>
      lowest === null || score < (breakdown[lowest] ?? 1) ? key : lowest,
    null,
  );
  return (
    <section className={className} aria-label="Confidence breakdown">
      <h3 className="text-[11px] font-medium uppercase tracking-[0.13em] text-text-tertiary">
        Confidence
      </h3>
      {confidence !== null && (
        <div className="mt-3 flex items-center gap-4">
          <ConfidenceRing value={confidence} />
          <div className="min-w-0">
            <p className="font-display text-[2rem] leading-none text-text-primary tabular-nums">
              {confidence.toFixed(2)}
            </p>
            <p className="mt-1 text-xs text-text-secondary">{tier(confidence)}</p>
          </div>
        </div>
      )}
      <dl className="mt-5 space-y-3.5">
        {rows.map(([key, score]) => {
          const pct = Math.round(score * 100);
          const isWeakest = key === weakest && score < 0.5;
          return (
            <div key={key}>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[13px] leading-tight text-text-secondary">{label(key)}</dt>
                <dd className="font-mono text-[11px] tabular-nums text-text-tertiary">{pct}%</dd>
              </div>
              <div
                className="mt-1.5 h-2 overflow-hidden rounded-full bg-text-tertiary/10 ring-1 ring-inset ring-border/40"
                role="meter"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={label(key)}
              >
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-700 ease-out",
                    pct === 0
                      ? "bg-transparent"
                      : isWeakest
                        ? "bg-gradient-to-r from-warning/60 to-warning"
                        : "bg-gradient-to-r from-accent/55 to-accent",
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </dl>
    </section>
  );
}
